import React from 'react';
import ClinicLogo from '../ClinicLogo';
import { PAYMENT_MODE_LABEL } from '../../constants/payments';
import { formatClinicDate, formatClinicDateTime } from '../../utils/clinicTime';
import { toPaisa, formatPaisa, formatSigned } from '../../utils/money';
import { receiptDoctorName, invoiceTotals } from './InvoiceReceipt';
import { pageMarginBoxes } from '../../utils/printDocument';

const STATUS_LABEL = { DRAFT: 'Draft', FINALIZED: 'Finalized', PAID: 'Paid', CANCELLED: 'Cancelled' };

const rs = (rupees) => `Rs. ${formatPaisa(toPaisa(rupees))}`;

/**
 * The A4 invoice: hidden on screen, printed with window.print() when the receipt paper setting is
 * A4. The 80 mm slip (InvoiceReceipt) is the default; this is the full-page alternative, carrying the
 * same figures: lines, discounts, the round off, and for cash what was handed over and the change.
 *
 * With `preview` it renders visibly and without the print rules, for the receipt dialog.
 */
const PrintableInvoice = ({ invoice, branding, preview = false }) => {
  if (!invoice) return null;
  const profile = branding?.profile || {};
  const name = profile.nameEnglish || 'Perveen Aslam Memorial Hospital';
  const nameUrdu = profile.nameUrdu || 'پروین اسلم میموریل ہسپتال';
  const contact = [profile.address, profile.phone && `Tel: ${profile.phone}`].filter(Boolean).join(' | ');
  const items = [...(invoice.items || [])].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
  const totalItemDiscount = items.reduce((s, i) => s + Number(i.discountAmount || 0), 0);
  // Everything the patient did not pay: the per-line discounts plus any bill-level one. Read off
  // the same fields the Discount column prints, so the sentence can never claim a different number
  // from the table above it.
  const totalSaving = totalItemDiscount + Number(invoice.discountAmount || 0);
  const totals = invoiceTotals(invoice);
  const money = (paisa) => `Rs. ${formatPaisa(paisa)}`;
  const doctor = receiptDoctorName(invoice);
  const isCash = invoice.paymentMode === 'CASH' && invoice.cashTendered != null;

  return (
    <div className={preview ? '' : 'hidden print:block print:p-0'} style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
      {!preview && (
        <style>{`
          @media print {
            body * { visibility: hidden; }
            .print\\:block, .print\\:block * { visibility: visible; }
            .print\\:block { position: absolute; left: 0; top: 0; width: 100%; }
            @page { margin: 15mm; ${pageMarginBoxes()} }
          }
        `}</style>
      )}

      {/* Header. The logo comes from the shared ClinicLogo, and the name, address and phone from
          Settings -> Branding, the same as the prescription. */}
      <div style={{ textAlign: 'center', borderBottom: '2px solid #003078', paddingBottom: '12px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '4px' }}>
          <ClinicLogo size={44} />
          <h1 style={{ fontSize: '22px', fontWeight: 'bold', color: '#003078', margin: 0 }}>{name}</h1>
        </div>
        <p style={{ fontSize: '14px', color: '#007f32', margin: 0, fontFamily: "'Noto Nastaliq Urdu', serif", direction: 'rtl' }}>
          {nameUrdu}
        </p>
        {contact && <p style={{ fontSize: '11px', color: '#666', margin: '6px 0 0 0' }}>{contact}</p>}
      </div>

      {/* Invoice meta */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', fontSize: '12px' }}>
        <div>
          <p style={{ margin: '2px 0' }}><strong>Invoice#:</strong> {invoice.invoiceNumber}</p>
          {/* The completed date, not the day the bill was raised. */}
          <p style={{ margin: '2px 0' }}><strong>Date:</strong> {formatClinicDate(invoice.paidAt, { day: 'numeric', month: 'long', year: 'numeric' })}</p>
          <p style={{ margin: '2px 0' }}><strong>Rx#:</strong> {invoice.prescription?.prescriptionNumber || '—'}</p>
          {doctor && <p style={{ margin: '2px 0' }}><strong>Doctor:</strong> {doctor}</p>}
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ margin: '2px 0' }}><strong>Patient:</strong> {invoice.patient?.firstName} {invoice.patient?.lastName}</p>
          <p style={{ margin: '2px 0' }}><strong>MRN:</strong> {invoice.patient?.mrn}</p>
          <p style={{ margin: '2px 0' }}><strong>Status:</strong> {STATUS_LABEL[invoice.status] || invoice.status}</p>
          {invoice.paidAt && <p style={{ margin: '2px 0' }}><strong>Paid:</strong> {formatClinicDateTime(invoice.paidAt)}</p>}
        </div>
      </div>

      {/* Items table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', marginBottom: '16px' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #333' }}>
            <th style={{ textAlign: 'left', padding: '6px 4px' }}>#</th>
            <th style={{ textAlign: 'left', padding: '6px 4px' }}>Item</th>
            <th style={{ textAlign: 'left', padding: '6px 4px' }}>Type</th>
            <th style={{ textAlign: 'center', padding: '6px 4px' }}>Qty</th>
            <th style={{ textAlign: 'right', padding: '6px 4px' }}>Unit Price</th>
            <th style={{ textAlign: 'right', padding: '6px 4px' }}>Discount</th>
            <th style={{ textAlign: 'right', padding: '6px 4px' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => (
            <tr key={item.id || idx} style={{ borderBottom: '1px solid #ddd' }}>
              <td style={{ padding: '5px 4px' }}>{idx + 1}</td>
              <td style={{ padding: '5px 4px' }}>
                {item.name}
                {item.description && <span style={{ color: '#888', fontSize: '10px' }}> ({item.description})</span>}
              </td>
              <td style={{ padding: '5px 4px' }}>
                {item.itemType === 'MEDICINE' ? 'Medicine' : item.itemType === 'MEDICAL_SERVICE' ? 'Service' : 'Extra'}
              </td>
              <td style={{ padding: '5px 4px', textAlign: 'center' }}>{item.quantity}</td>
              <td style={{ padding: '5px 4px', textAlign: 'right' }}>{rs(item.unitPrice)}</td>
              {/* Bold black, and no minus sign: the column is headed Discount, so the sign only
                  restated it, and green read as a note rather than part of the figures. */}
              <td style={{
                padding: '5px 4px',
                textAlign: 'right',
                fontWeight: Number(item.discountAmount) > 0 ? 'bold' : 'normal',
                color: Number(item.discountAmount) > 0 ? '#111827' : '#999',
              }}>
                {Number(item.discountAmount) > 0 ? rs(item.discountAmount) : '—'}
              </td>
              <td style={{ padding: '5px 4px', textAlign: 'right', fontWeight: 'bold' }}>{rs(item.total)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          {/* Same working as the invoice page and the slip: full price, what was taken off, what was charged. */}
          <tr style={{ borderTop: '2px solid #333' }}>
            <td colSpan={6} style={{ padding: '6px 4px', textAlign: 'right', fontWeight: 'bold' }}>Total Amount</td>
            <td style={{ padding: '6px 4px', textAlign: 'right', fontWeight: 'bold' }}>{money(totals.gross)}</td>
          </tr>
          {totals.discount > 0 && (
            <tr>
              <td colSpan={6} style={{ padding: '4px 4px', textAlign: 'right', color: '#007f32' }}>Discount</td>
              <td style={{ padding: '4px 4px', textAlign: 'right', color: '#007f32' }}>- {money(totals.discount)}</td>
            </tr>
          )}
          {totals.tax > 0 && (
            <tr>
              <td colSpan={6} style={{ padding: '4px 4px', textAlign: 'right' }}>Tax</td>
              <td style={{ padding: '4px 4px', textAlign: 'right' }}>{money(totals.tax)}</td>
            </tr>
          )}
          {totals.rounding !== 0 && (
            <tr>
              <td colSpan={6} style={{ padding: '4px 4px', textAlign: 'right' }}>Round off</td>
              <td style={{ padding: '4px 4px', textAlign: 'right' }}>{formatSigned(invoice.roundingAdjustment)}</td>
            </tr>
          )}
          <tr style={{ borderTop: '1px solid #999' }}>
            <td colSpan={6} style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 'bold', fontSize: '14px' }}>Final Amount</td>
            <td style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 'bold', fontSize: '14px' }}>{rs(invoice.grandTotal)}</td>
          </tr>
        </tfoot>
      </table>

      {/* Payment info */}
      {invoice.status === 'PAID' && (
        <div style={{ marginBottom: '16px', fontSize: '12px', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}>
          {Number(invoice.grandTotal) > 0 ? (
            <>
              <p style={{ margin: '2px 0' }}><strong>Amount Paid:</strong> <strong>{rs(invoice.amountPaid)}</strong></p>
              <p style={{ margin: '2px 0' }}><strong>Payment Mode:</strong> <strong>{PAYMENT_MODE_LABEL[invoice.paymentMode] || invoice.paymentMode || '—'}</strong></p>
              {invoice.paymentReference && <p style={{ margin: '2px 0' }}><strong>Reference:</strong> {invoice.paymentReference}</p>}
              {isCash && (
                <>
                  <p style={{ margin: '2px 0' }}><strong>Cash Received:</strong> {rs(invoice.cashTendered)}</p>
                  <p style={{ margin: '2px 0' }}><strong>Change Given:</strong> {rs(invoice.changeReturned)}</p>
                </>
              )}
            </>
          ) : (
            <p style={{ margin: '2px 0' }}><strong>No charge</strong></p>
          )}
        </div>
      )}

      {/* What the hospital took off this bill, said in Urdu because it is the patient who reads it.
          The figure stays in Western digits and Latin "Rs." per the house rule for numbers in Urdu
          text. Only shown when something was actually taken off. */}
      {totalSaving > 0 && (
        <div style={{
          border: '1px solid #007f32',
          background: '#ebf8f0',
          borderRadius: '4px',
          padding: '8px 10px',
          marginBottom: '12px',
          textAlign: 'center',
        }}>
          <p style={{
            margin: 0,
            fontFamily: "'Noto Nastaliq Urdu', serif",
            direction: 'rtl',
            fontSize: '14px',
            lineHeight: 2,
            color: '#005523',
          }}>
            {/* The Latin amount is isolated (U+2066..U+2069) so bidi cannot reorder "Rs. 570", and
                given real margins: Nastaliq sets tightly. */}
            ہسپتال کی جانب سے آپ کو
            <strong style={{ margin: '0 6px', direction: 'ltr', unicodeBidi: 'isolate', fontFamily: 'Arial, Helvetica, sans-serif' }}>
              {'⁦'}{rs(totalSaving)}{'⁩'}
            </strong>
            کی رعایت دی گئی ہے
          </p>
        </div>
      )}

      {/* The return policy, the same notice the 80 mm slip carries, because A4 is the other paper a
          patient is handed and not a different message (owner, 2026-10-05). Darker than the rest of
          the footer: it is a condition of the sale, not a pleasantry. */}
      <div style={{ borderTop: '1px solid #ccc', paddingTop: '10px', marginBottom: '10px', fontSize: '11px', color: '#444', textAlign: 'center' }}>
        <p style={{ margin: '2px 0' }}>Opened or loose tablets cannot be returned or exchanged.</p>
        <p style={{
          margin: '4px 0 0 0',
          fontFamily: "'Noto Nastaliq Urdu', serif",
          direction: 'rtl',
          fontSize: '13px',
          lineHeight: 2,
        }}>
          کھلی ہوئی یا کٹی ہوئی گولیاں واپس یا تبدیل نہیں ہوں گی
        </p>
      </div>

      {/* Footer */}
      <div style={{ borderTop: '1px solid #ccc', paddingTop: '10px', fontSize: '10px', color: '#888', textAlign: 'center' }}>
        <p style={{ margin: '2px 0' }}>Thank you for visiting {name}</p>
        {/* The same closing line the 80 mm receipt carries, because A4 is the other paper a
            patient is handed and not a different message. */}
        <p style={{
          margin: '6px 0 2px 0',
          fontFamily: "'Noto Nastaliq Urdu', serif",
          direction: 'rtl',
          fontSize: '12px',
          lineHeight: 2,
          color: '#555',
        }}>
          ہمیں اپنی دعاؤں میں یاد رکھیں
        </p>
        <p style={{ margin: '2px 0' }}>This is a computer-generated invoice.</p>
      </div>
    </div>
  );
};

export default PrintableInvoice;
